import React, { useState, useEffect, useRef } from 'react';
import * as Alert from '@radix-ui/react-alert-dialog';
import { XIcon, UploadIcon } from '@heroicons/react/outline';

const SIGNATURE_STYLES = [
  { id: 'style1', fontFamily: 'Dancing Script', displayName: 'Style 1' },
  { id: 'style2', fontFamily: 'Caveat', displayName: 'Style 2' },
  { id: 'style3', fontFamily: 'Kalam', displayName: 'Style 3' }
];

// Helper function to convert File to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = error => reject(error);
  });
};

const SignatureDialog = ({
  open,
  onOpenChange,
  onSave,
  onDelete,
  initialSignature = {},
  currentUserName = '',
}) => {
  // State
  const [signatureType, setSignatureType] = useState(initialSignature.signatureType || 'style');
  const [signatureText, setSignatureText] = useState(initialSignature.signatureText || currentUserName || '');
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [stylePreviewSignature, setStylePreviewSignature] = useState(initialSignature.previewUrl || null);
  const [uploadPreviewSignature, setUploadPreviewSignature] = useState(initialSignature.previewUrl || null);
  const [uploadedSignature, setUploadedSignature] = useState(null);
  const [signatureHasChanges, setSignatureHasChanges] = useState(false);
  const [isSavingSignature, setIsSavingSignature] = useState(false);
  const [signatureError, setSignatureError] = useState('');
  const [signatureSuccess, setSignatureSuccess] = useState('');
  const [signatureSaveCompleted, setSignatureSaveCompleted] = useState(false);

  // Init state from props WHEN the dialog opens, not on every re-render
  // Business logic: We only want to hydrate the dialog with initial values once per
  // open cycle. Re-renders triggered by global state/auth changes should not wipe out
  // the user's in-progress selection and thereby disable the Save button. We track
  // whether we've already initialized for the current open cycle using a ref flag.
  const hasInitializedForOpenRef = useRef(false);
  useEffect(() => {
    if (open && !hasInitializedForOpenRef.current) {
      // 1) Set any pre-selected style from props
      if (initialSignature.signatureStyle) {
        const style = SIGNATURE_STYLES.find(s => s.id === initialSignature.signatureStyle);
        if (style) {
          setSelectedStyle(style);
        }
      }

      // 2) Seed the preview according to initial signature type (if provided)
      if (initialSignature.signatureType === 'style') {
        setStylePreviewSignature(initialSignature.previewUrl || null);
      } else if (initialSignature.signatureType === 'upload') {
        setUploadPreviewSignature(initialSignature.previewUrl || null);
      } else if (initialSignature.previewUrl) {
        // Default case - set both if type not specified
        setStylePreviewSignature(initialSignature.previewUrl);
        setUploadPreviewSignature(initialSignature.previewUrl);
      }

      // 3) At the start of an open cycle with no existing signature, there are no changes yet
      if (!initialSignature.previewUrl && !initialSignature.signatureStyle) {
        setSignatureHasChanges(false);
      }

      hasInitializedForOpenRef.current = true;
    }

    // When the dialog closes, allow a fresh initialization on the next open
    if (!open && hasInitializedForOpenRef.current) {
      hasInitializedForOpenRef.current = false;
    }
  }, [open, initialSignature]);

  // Canvas-based signature generation
  const generateSignatureImage = (style) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    canvas.width = 400;
    canvas.height = 100;
    
    // Set font style
    ctx.font = `36px ${style.fontFamily}`;
    ctx.fillStyle = 'black';
    
    // Center the text
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Draw the signature using custom text
    ctx.fillText(signatureText, canvas.width/2, canvas.height/2);
    
    return canvas.toDataURL('image/png');
  };

  // Event handlers
  const handleStyleSelect = (style) => {
    setSelectedStyle(style);
    const signatureImage = generateSignatureImage(style);
    setStylePreviewSignature(signatureImage);
    setSignatureHasChanges(true);
    setSignatureError('');
    setSignatureSuccess('');
  };

  const handleSignatureTextChange = (e) => {
    const newText = e.target.value;
    setSignatureText(newText);
    if (selectedStyle) {
      const signatureImage = generateSignatureImage(selectedStyle);
      setStylePreviewSignature(signatureImage);
    }
    setSignatureHasChanges(true);
    setSignatureError('');
    setSignatureSuccess('');
  };

  const validateFile = (file) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/tiff'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      return 'File must be a JPG, PNG, or TIFF image';
    }
    if (file.size > maxSize) {
      return 'File must be smaller than 5MB';
    }
    return null;
  };

  const handleFileUpload = (file) => {
    const error = validateFile(file);
    if (error) {
      setSignatureError(error);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadPreviewSignature(reader.result);
      setUploadedSignature(file);
      // Always set signature type to 'upload' when uploading a signature file
      setSignatureType('upload');
      setSignatureHasChanges(true);
      setSignatureError('');
      setSignatureSuccess('');
    };
    reader.readAsDataURL(file);
  };

  const handleSignatureTypeChange = (type) => {
    // Only update if the type is different
    if (type !== signatureType) {
      // Clear any error/success messages
      setSignatureError('');
      setSignatureSuccess('');
      
      // Update the signature type
      setSignatureType(type);
      
      // Reset preview and uploaded signature when switching to upload type
      if (type === 'upload') {
        setStylePreviewSignature(null);
      } else {
        setUploadPreviewSignature(null);
        setUploadedSignature(null);
      }
      
      // If there are unsaved changes, set hasChanges to true
      if (type === 'style' && selectedStyle) {
        setSignatureHasChanges(true);
      } else if (type === 'upload' && uploadedSignature) {
        setSignatureHasChanges(true);
      }
    }
  };

  const handleCancel = () => {
    // Reset all states
    setStylePreviewSignature(initialSignature.previewUrl || null);
    setUploadPreviewSignature(initialSignature.previewUrl || null);
    setSelectedStyle(null);
    setUploadedSignature(null);
    setSignatureHasChanges(false);
    setSignatureError('');
    setSignatureSuccess('');
    setSignatureSaveCompleted(false);
  };

  const handleCloseDialog = () => {
    onOpenChange(false);
    setSignatureSaveCompleted(false);
  };

  const handleSave = async () => {
    setIsSavingSignature(true);
    setSignatureError('');
    setSignatureSuccess('');
    setSignatureSaveCompleted(false);
    
    try {
      // Prepare signature data to be saved
      let signatureData = {};
      
      if (signatureType === 'style') {
        // Handle digital signature if present
        if (stylePreviewSignature && selectedStyle) {
          // Remove the "data:image/png;base64," prefix if present
          const signatureBase64 = stylePreviewSignature.includes('base64,') 
            ? stylePreviewSignature.split(',')[1] 
            : stylePreviewSignature;
            
          signatureData = {
            data: signatureBase64,
            type: 'image/png',
            name: 'digital-signature.png',
            style: selectedStyle.id,
            text: signatureText,
            signatureType: 'style',
            signatureMetadata: {
              signatureStyle: selectedStyle.id,
              signatureText: signatureText,
              signatureType: 'style'
            }
          };
        }
      } else {
        // Handle uploaded signature
        if (uploadedSignature) {
          const signatureBase64 = await fileToBase64(uploadedSignature);
          signatureData = {
            data: signatureBase64,
            type: uploadedSignature.type,
            name: uploadedSignature.name,
            signatureType: 'upload',
            signatureMetadata: {
              signatureType: 'upload'
            }
          };
        } else if (uploadPreviewSignature) {
          // For existing uploaded signatures without a new file
          signatureData = {
            signatureType: 'upload',
            signatureMetadata: {
              signatureType: 'upload'
            }
          };
        }
      }

      // Only proceed if we have updates
      if (Object.keys(signatureData).length > 0) {
        // Call the parent's save handler
        await onSave(signatureData);
        
        setSignatureHasChanges(false);
        setSignatureSuccess('Signature updated successfully');
        setSignatureSaveCompleted(true);
      } else {
        setSignatureError('No changes to save');
      }

    } catch (err) {
      console.error('Error saving signature:', err);
      setSignatureError('Failed to save signature. Please try again.');
    } finally {
      setIsSavingSignature(false);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete('signature');
    }
  };

  return (
    <Alert.Root 
      open={open} 
      onOpenChange={(newOpen) => {
        // Always allow closing if explicitly requested
        if (!newOpen) {
          setSignatureSaveCompleted(false);
          onOpenChange(false);
        }
      }}
    >
      <Alert.Portal>
        <Alert.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50" />
        <Alert.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[90vw] max-w-[700px] max-h-[90vh] overflow-y-auto">
          <Alert.Title className="text-xl font-bold mb-4 flex items-center justify-between">
            <span>{initialSignature.customTitle || "Digital Signature"}</span>
            {initialSignature.isOnboarding ? (
              <></>
            ) : (
              <button 
                className="text-gray-400 hover:text-gray-500"
                onClick={() => onOpenChange(false)}
              >
                <XIcon className="w-6 h-6" />
              </button>
            )}
          </Alert.Title>
          
          <Alert.Description asChild>
            <div className="space-y-4">
              {initialSignature.customDescription ? (
                <p className="text-sm font-semibold text-gray-700 p-3 bg-blue-50 border border-blue-100 rounded-md">
                  {initialSignature.customDescription}
                </p>
              ) : (
                <p className="text-sm text-gray-500">
                  Your signature will appear in generated documents that you review and approve.
                </p>
              )}
              
              <p className="text-sm text-gray-500 font-medium">
                Choose one of the following options to create your signature:
              </p>
              
              {/* Signature Type Selection Tabs */}
              <div className="border-b border-gray-200">
                <div className="flex">
                  <button
                    onClick={() => handleSignatureTypeChange('style')}
                    disabled={signatureSaveCompleted}
                    className={`py-2 px-4 font-medium text-sm focus:outline-none ${
                      signatureType === 'style'
                        ? 'border-b-2 border-primary text-primary'
                        : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } ${signatureSaveCompleted ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Choose a style
                  </button>
                  <button
                    onClick={() => handleSignatureTypeChange('upload')}
                    disabled={signatureSaveCompleted}
                    className={`py-2 px-4 font-medium text-sm focus:outline-none ${
                      signatureType === 'upload'
                        ? 'border-b-2 border-primary text-primary'
                        : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } ${signatureSaveCompleted ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Upload your own
                  </button>
                </div>
              </div>

              {/* Different UI based on the selected signature type */}
              {signatureType === 'style' ? (
                <>
                  {/* Signature Text Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Your Signature Text
                    </label>
                    <input
                      type="text"
                      value={signatureText}
                      onChange={handleSignatureTextChange}
                      placeholder="Enter your signature text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                      disabled={signatureSaveCompleted}
                    />
                  </div>

                  {/* Signature Style Options */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select a Style
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {SIGNATURE_STYLES.map((style) => (
                        <button
                          key={style.id}
                          onClick={() => handleStyleSelect(style)}
                          disabled={signatureSaveCompleted}
                          className={`p-4 border-2 rounded-lg transition-all ${
                            selectedStyle?.id === style.id 
                              ? 'border-primary bg-primary/5' 
                              : 'border-gray-200 hover:border-primary/30'
                          } ${signatureSaveCompleted ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <div 
                            className="h-16 flex items-center justify-center"
                            style={{ fontFamily: style.fontFamily }}
                          >
                            <span className="text-2xl">{signatureText}</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-2">{style.displayName}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Preview Section for Style-based signatures */}
                  {stylePreviewSignature && (
                    <div className="border-2 border-primary/30 bg-primary/5 rounded-lg p-4">
                      <div className="relative w-full max-w-[200px] mx-auto">
                        <img 
                          src={stylePreviewSignature} 
                          alt="Signature preview" 
                          className="w-full h-auto rounded-md"
                        />
                        {!signatureSaveCompleted && onDelete && (
                          <button
                            onClick={handleDelete}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <XIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Signature Image
                  </label>
                  <p className="text-sm text-gray-500 mb-3">
                    Upload an image of your signature with a white or transparent background for best results.
                  </p>
                  
                  {uploadPreviewSignature ? (
                    <div className="border-2 border-primary/30 bg-primary/5 rounded-lg p-4">
                      <div className="relative w-full max-w-[200px] mx-auto">
                        <img 
                          src={uploadPreviewSignature} 
                          alt="Signature preview" 
                          className="w-full h-auto rounded-md"
                        />
                        {!signatureSaveCompleted && (
                          <button
                            onClick={() => {
                              setUploadPreviewSignature(null);
                              setUploadedSignature(null);
                              setSignatureHasChanges(true);
                            }}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <XIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div 
                      className={`relative border-2 border-dashed rounded-lg p-4 border-gray-300 hover:border-primary/50 transition-colors duration-200 ${signatureSaveCompleted ? 'opacity-50 pointer-events-none' : ''}`}
                      onDragOver={(e) => {
                        if (signatureSaveCompleted) return;
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.add('border-primary/50', 'bg-primary/5');
                      }}
                      onDragEnter={(e) => {
                        if (signatureSaveCompleted) return;
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.add('border-primary/50', 'bg-primary/5');
                      }}
                      onDragLeave={(e) => {
                        if (signatureSaveCompleted) return;
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.remove('border-primary/50', 'bg-primary/5');
                      }}
                      onDrop={(e) => {
                        if (signatureSaveCompleted) return;
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.classList.remove('border-primary/50', 'bg-primary/5');
                        
                        const files = e.dataTransfer.files;
                        if (files && files.length > 0) {
                          handleFileUpload(files[0]);
                        }
                      }}
                    >
                      <div className="flex flex-col items-center justify-center">
                        <UploadIcon className="w-8 h-8 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500">
                          Drag and drop or click to upload
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          PNG, JPG, TIFF (max 5MB)
                        </p>
                      </div>
                      <input
                        type="file"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        onChange={(e) => handleFileUpload(e.target.files[0])}
                        accept=".jpg,.jpeg,.png,.tiff"
                        disabled={signatureSaveCompleted}
                      />
                    </div>
                  )}
                </div>
              )}
              
              {/* Display success or error messages in the dialog */}
              {signatureSuccess && (
                <div className="p-3 bg-green-100 text-green-700 rounded-md text-sm">
                  {signatureSuccess}
                </div>
              )}

              {signatureError && (
                <div className="p-3 bg-red-100 text-red-700 rounded-md text-sm">
                  {signatureError}
                </div>
              )}
            </div>
          </Alert.Description>
          
          <div className="flex justify-end gap-3 mt-6">
            {!signatureSaveCompleted ? (
              <>
                {!initialSignature.isOnboarding && (
                  <Alert.Cancel asChild>
                    <button
                      onClick={handleCancel}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                      Cancel
                    </button>
                  </Alert.Cancel>
                )}
                <button
                  onClick={handleSave}
                  disabled={!signatureHasChanges || isSavingSignature}
                  className="px-4 py-2 text-sm font-semibold text-white bg-primary border border-transparent rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                >
                  {isSavingSignature ? 'Saving...' : 'Save Signature'}
                </button>
              </>
            ) : (
              <button
                onClick={handleCloseDialog}
                className="px-4 py-2 text-sm font-semibold text-white bg-primary border border-transparent rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Finish
              </button>
            )}
          </div>
        </Alert.Content>
      </Alert.Portal>
    </Alert.Root>
  );
};

export default SignatureDialog; 